import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValue, useSpring, useReducedMotion } from "framer-motion";

const INTERACTIVE = "a, button, summary, [role='button'], input, select, textarea, [data-cursor]";

// Inspection reticle cursor: a subtle orange crosshair follows the pointer
// everywhere; over interactive elements it expands into camera-viewfinder
// corner brackets framing the target — the inspect → verify identity, as UI.
// A small scanning dot always marks the exact pointer. Contextual label pills
// on [data-cursor] targets are preserved. Desktop (fine pointer) only;
// disables itself under reduced motion.
export function Cursor() {
  const reduce = useReducedMotion();
  const [label, setLabel] = useState(null);
  const [frame, setFrame] = useState(null); // {x,y,w,h} of hovered target
  const [enabled, setEnabled] = useState(false);
  const hoverEl = useRef(null);
  const x = useMotionValue(-100);
  const y = useMotionValue(-100);
  const sx = useSpring(x, { stiffness: 550, damping: 42, mass: 0.55 });
  const sy = useSpring(y, { stiffness: 550, damping: 42, mass: 0.55 });

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    if (!fine || reduce) return;
    setEnabled(true);
    document.documentElement.classList.add("dcc-custom-cursor");

    const PAD = 8;
    const measure = (el) => {
      const r = el.getBoundingClientRect();
      return { x: r.left - PAD, y: r.top - PAD, w: r.width + PAD * 2, h: r.height + PAD * 2 };
    };
    const move = (e) => {
      x.set(e.clientX);
      y.set(e.clientY);
      const tagged = e.target.closest?.("[data-cursor]");
      setLabel(tagged ? tagged.getAttribute("data-cursor") : null);
      const t = e.target.closest?.(INTERACTIVE) || null;
      if (t !== hoverEl.current) {
        hoverEl.current = t;
        setFrame(t ? measure(t) : null);
      }
    };
    const remeasure = () => {
      if (hoverEl.current && document.contains(hoverEl.current)) setFrame(measure(hoverEl.current));
    };
    window.addEventListener("mousemove", move, { passive: true });
    window.addEventListener("scroll", remeasure, { passive: true, capture: true });
    window.addEventListener("resize", remeasure);
    return () => {
      document.documentElement.classList.remove("dcc-custom-cursor");
      window.removeEventListener("mousemove", move);
      window.removeEventListener("scroll", remeasure, true);
      window.removeEventListener("resize", remeasure);
    };
  }, [reduce, x, y]);

  if (!enabled) return null;

  return (
    <>
      {/* scanning dot — always at the exact pointer */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[92] h-1 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-dcc"
        style={{ x: sx, y: sy }}
      />

      {/* idle crosshair — visible everywhere, fades when the frame takes over */}
      <motion.div
        aria-hidden="true"
        className="pointer-events-none fixed left-0 top-0 z-[91] -translate-x-1/2 -translate-y-1/2"
        style={{ x: sx, y: sy }}
        animate={{ opacity: frame ? 0 : 0.5 }}
        transition={{ duration: 0.18 }}
      >
        <span className="absolute left-1/2 top-1/2 h-[15px] w-px -translate-x-1/2 -translate-y-1/2 bg-dcc" />
        <span className="absolute left-1/2 top-1/2 h-px w-[15px] -translate-x-1/2 -translate-y-1/2 bg-dcc" />
      </motion.div>

      {/* viewfinder frame — corner brackets around the hovered target */}
      <AnimatePresence>
        {frame && (
          <motion.div
            key="reticle-frame"
            aria-hidden="true"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, left: frame.x, top: frame.y, width: frame.w, height: frame.h }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-none fixed z-[91]"
          >
            <span className="absolute -left-px -top-px h-3 w-3 border-l-2 border-t-2 border-dcc" />
            <span className="absolute -right-px -top-px h-3 w-3 border-r-2 border-t-2 border-dcc" />
            <span className="absolute -bottom-px -left-px h-3 w-3 border-b-2 border-l-2 border-dcc" />
            <span className="absolute -bottom-px -right-px h-3 w-3 border-b-2 border-r-2 border-dcc" />
          </motion.div>
        )}
      </AnimatePresence>

      {/* contextual label pill (data-cursor) */}
      {label && (
        <motion.div
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="pointer-events-none fixed left-0 top-0 z-[92] -translate-y-1/2 translate-x-5 bg-dcc px-3 py-1.5 font-mono text-xs uppercase tracking-[0.25em] text-white"
          style={{ x: sx, y: sy }}
        >
          {label}
        </motion.div>
      )}
    </>
  );
}
